/**
 * Decides when a failed API request should send the user to the 503 screen
 * (RAP-220), kept free of axios/window so the rules are unit-testable.
 *
 * Three outcomes:
 *  - "redirect": the server told us it is unavailable (a 503: maintenance mode,
 *    or the ALB with no healthy targets). Go to the 503 screen now.
 *  - "confirm": the failure LOOKS like an outage but a single request can fail
 *    this way while the API is fine: a network error (server down, but also a
 *    CORS rejection or a dropped connection on one request) or an ALB 502/504
 *    (backend gone, but also one slow request hitting the ALB idle timeout).
 *    Ping the health endpoint, and redirect only if that fails too.
 *  - "ignore": not an outage, or redirecting would be wrong (cancelled
 *    request, browser offline, already on the 503 screen, caller opted out).
 */

export const SERVICE_UNAVAILABLE_PATH = "/error-503";

export type ApiFailureAction = "confirm" | "ignore" | "redirect";

export type ServiceUnavailableReason = "maintenance" | "outage";

export interface ApiFailureInput {
  /** Axios error code, e.g. "ERR_NETWORK", "ECONNABORTED", "ERR_CANCELED". */
  code?: string;
  /** window.location.pathname at the time of the failure. */
  currentPath: string;
  /** axios.isCancel(error): AbortController / navigation cancellation. */
  isCancel: boolean;
  /** navigator.onLine. */
  online: boolean;
  /** config.skipMaintenanceRedirect: route uses 503 for a business condition. */
  skipMaintenanceRedirect: boolean;
  /** HTTP status, undefined when no response arrived. */
  status?: number;
}

export function classifyApiFailure(input: ApiFailureInput): ApiFailureAction {
  if (input.isCancel || input.code === "ERR_CANCELED") return "ignore";
  if (input.skipMaintenanceRedirect) return "ignore";
  if (input.currentPath === SERVICE_UNAVAILABLE_PATH) return "ignore";

  if (input.status === 503) return "redirect";
  if (input.status === 502 || input.status === 504) return input.online ? "confirm" : "ignore";
  if (input.status !== undefined) return "ignore";

  // No response. Only a genuine network error counts: an axios timeout
  // (ECONNABORTED/ETIMEDOUT) is one slow request, not proof the API is down.
  if (input.code !== "ERR_NETWORK") return "ignore";
  // Offline is the user's connection, not our outage. The 503 screen would be
  // misleading and its "try again" would just fail again.
  if (!input.online) return "ignore";
  return "confirm";
}

/** The backend's maintenance-mode 503 body (routes/config.ts) mentions maintenance. */
export function reasonFromResponse(
  status: number | undefined,
  data: unknown
): ServiceUnavailableReason {
  if (status !== 503 || typeof data !== "object" || data === null) return "outage";
  const message = (data as { message?: unknown }).message;
  return typeof message === "string" && /maintenance/i.test(message) ? "maintenance" : "outage";
}

/** Same open-redirect rules as MagicLinkRedeem: a single-slash relative path only. */
export function isSafeReturnPath(path: unknown): path is string {
  if (typeof path !== "string" || !path.startsWith("/")) return false;
  // The URL parser strips tab/LF/CR before parsing, so "/\t/evil.com" would
  // pass the checks below and then navigate to "//evil.com".
  if (/[\u0000-\u001f\u007f]/.test(path)) return false;
  if (path.startsWith("//") || path.startsWith("/\\")) return false;
  if (path.includes("..") || path.includes("://")) return false;
  const pathname = path.split(/[?#]/)[0];
  return pathname !== SERVICE_UNAVAILABLE_PATH;
}

/**
 * Build the 503 screen URL, carrying where the user was so "Try again" can
 * return there. Only the pathname is carried: a query or hash can hold search
 * terms or other user input, which shouldn't be copied into another URL
 * (same reason as toPathOnlyEndpoint in axios.ts).
 */
export function buildServiceUnavailableUrl(
  reason: ServiceUnavailableReason,
  returnTo: string
): string {
  const params = new URLSearchParams({ reason });
  const pathname = returnTo.split(/[?#]/)[0];
  if (isSafeReturnPath(pathname) && pathname !== "/") params.set("redirect", pathname);
  return `${SERVICE_UNAVAILABLE_PATH}?${params.toString()}`;
}

/**
 * Ping GET {baseURL}/health with fetch (not apiClient, so a failure here can't
 * re-enter the interceptor). Resolves false only when the API is genuinely not
 * answering: network failure, timeout, or a gateway/unavailable status.
 */
export async function isApiReachable(
  baseURL: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs = 8000
): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetchImpl(`${baseURL.replace(/\/$/, "")}/health`, {
      cache: "no-store",
      signal: controller.signal,
    });
    return ![502, 503, 504].includes(response.status);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
